/**
 * Premium Home — Apple/Linear/Notion-inspired full landing page.
 * Sections: Hero · Pillars · How it works · Featured tools · Stats · Testimonials · FAQ · CTA · Footer
 * Strict palette: #A855F7 + #111111 + #FFFFFF + slate. Outfit headings, Inter body.
 */
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Sparkles, Play, GraduationCap, Youtube, Pen,
  Lightbulb, Wand2, Rocket, Check, Star, Plus, Minus,
  TrendingUp, Hash, FileText, Search, Mic, Image as ImageIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import PremiumNav from "./PremiumNav";
import PremiumFooter from "./PremiumFooter";
import { PremiumButton, PremiumCard, Pill } from "./Primitives";

const PILLAR_CARDS = [
  { Icon: GraduationCap, title: "Teaching Suite", desc: "PPT, quizzes, lesson plans, worksheets — instantly.", to: "/teaching" },
  { Icon: Youtube,       title: "YouTube Suite",  desc: "Channel audit, scripts, SEO, thumbnails.",            to: "/youtube-suite" },
  { Icon: Pen,           title: "Content Suite",  desc: "Posts, blogs, emails, ads — all channels.",           to: "/content-suite" },
  { Icon: Sparkles,      title: "AI Studio",      desc: "Every AI tool in one elegant workspace.",             to: "/ai-studio" },
];

const TRUSTED_BY = ["Teachers", "Coaching Institutes", "Schools & Colleges", "YouTubers", "Podcasters", "Agencies", "Freelancers", "Founders"];

const HOW_IT_WORKS = [
  { step: "01", Icon: Lightbulb, title: "Pick your goal", desc: "Channel audit, lesson plan, blog post, ad copy — start with what you need to ship today." },
  { step: "02", Icon: Wand2,     title: "Let AI do the work", desc: "Claude + GPT + Gemini behind one elegant interface. We pick the right model for each job." },
  { step: "03", Icon: Rocket,    title: "Edit, export, ship", desc: "Inline edits, branded exports, share links. Done in minutes — not days." },
];

const FEATURED_TOOLS = [
  { Icon: Youtube,     title: "Channel Assessment", desc: "Paste any YT URL → 25-metric audit + 30-day plan.", badge: "NEW", to: "/youtube-suite/channel-assessment" },
  { Icon: Search,      title: "AI Keyword Generator", desc: "Live SerpAPI + Claude — real search volumes.",       badge: "LIVE DATA", to: "/tools/seo/ai" },
  { Icon: GraduationCap, title: "AI PPT Generator", desc: "Topic → editable PPTX in 60 seconds.",                badge: "POPULAR", to: "/tools/ai-slides" },
  { Icon: Mic,         title: "Auto Captions",     desc: "Upload video → branded SRT captions + share page.",   badge: "NEW", to: "/tools/auto-captions" },
  { Icon: Hash,        title: "Hashtag Generator", desc: "Niche-perfect hashtag packs across Instagram, X, LinkedIn.", to: "/free/hashtag-generator" },
  { Icon: FileText,    title: "Word Counter",      desc: "Words, characters, reading time — instantly.",         to: "/free/word-counter" },
];

const TESTIMONIALS = [
  {
    quote: "I cut my lesson-prep from 6 hours a week to 30 minutes. The PPT generator is genuinely the best I've used.",
    name: "Priyanka Sharma",
    role: "Physics Teacher · DPS Noida",
    rating: 5,
  },
  {
    quote: "Channel Assessment told me exactly why my last 5 videos flopped — and what to ship next. Sub count up 4× in 60 days.",
    name: "Rohan Mehra",
    role: "Creator · 84K subs",
    rating: 5,
  },
  {
    quote: "Our agency used to juggle Jasper + Canva + 3 SEO tools. MarkeeMix replaced all 5 — and the AI is sharper.",
    name: "Ananya Iyer",
    role: "Founder · BrightCopy Agency",
    rating: 5,
  },
];

const FAQS = [
  { q: "Is there a free tier?", a: "Yes — every account includes free access to 15+ utility tools (word counter, hashtag generator, ROI calculator and more) and 5 daily AI credits across the AI Studio. Upgrade only when you hit your limit." },
  { q: "How does MarkeeMix compare to ChatGPT or Jasper?", a: "ChatGPT is a blank chat. MarkeeMix is a workflow — each tool has a tuned prompt + UI + export pipeline. You're 10× faster because you don't have to prompt-engineer anything." },
  { q: "Which AI models power MarkeeMix?", a: "We route every task to the best model — Claude Haiku 4.5 for reasoning, Gemini 3 for vision, GPT-5.2 for long-form, all behind one Emergent LLM key. You never manage API keys." },
  { q: "Can I export everything?", a: "Yes — PPT, PDF, DOCX, CSV, SRT, JSON. Every tool ships with at least one export format and a copy-to-clipboard." },
  { q: "Do you offer team / agency plans?", a: "Yes — multi-seat workspaces with shared brand assets, approval workflows, role-based dashboards and white-label client portals. Check /agencies." },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, ease: [0.22, 0.61, 0.36, 1], delay },
});

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-5 text-left group"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-heading text-base sm:text-lg font-semibold text-[#111111] group-hover:text-[#A855F7] transition-colors">
          {q}
        </span>
        <span className="w-7 h-7 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 group-hover:border-[#A855F7] group-hover:text-[#A855F7] transition-colors">
          {open ? <Minus className="w-3.5 h-3.5"/> : <Plus className="w-3.5 h-3.5"/>}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-sm sm:text-base text-slate-600 leading-relaxed pr-10">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Made-in-India Indic AI stack showcase ───────────────────────────
function IndicHeroSection() {
  const navigate = useNavigate();
  const [voicePlaying, setVoicePlaying] = React.useState(false);
  const [demoIn, setDemoIn] = React.useState("Marketing made simple with AI.");
  const [demoOut, setDemoOut] = React.useState("");
  const [demoBusy, setDemoBusy] = React.useState(false);
  const audioRef = React.useRef(null);

  const play = (spk) => {
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.src = ""; } catch (_e) { /* ignore */ }
    }
    setVoicePlaying(spk);
    const a = new Audio(`${process.env.REACT_APP_BACKEND_URL || ""}/api/sarvam/tts/preview/${spk}?lang=hi-IN`);
    audioRef.current = a;
    a.play().catch(() => setVoicePlaying(false));
    a.onended = () => setVoicePlaying(false);
  };

  const translate = async () => {
    if (!demoIn.trim()) return;
    setDemoBusy(true); setDemoOut("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/sarvam/translate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: demoIn, source_language_code: "en-IN", target_language_code: "hi-IN", mode: "modern-colloquial" }),
      });
      const d = await res.json();
      setDemoOut(d?.result?.translated_text || "");
    } catch { /* ignore */ }
    finally { setDemoBusy(false); }
  };

  const voices = [
    { id: "shreya", label: "Shreya", gender: "F" },
    { id: "priya", label: "Priya", gender: "F" },
    { id: "kavitha", label: "Kavitha", gender: "F" },
    { id: "simran", label: "Simran", gender: "F" },
    { id: "aditya", label: "Aditya", gender: "M" },
    { id: "rahul", label: "Rahul", gender: "M" },
    { id: "kabir", label: "Kabir", gender: "M" },
    { id: "vijay", label: "Vijay", gender: "M" },
  ];

  return (
    <section className="relative bg-gradient-to-b from-orange-50/50 via-white to-white border-b border-slate-200/60 overflow-hidden" data-testid="indic-hero-section">
      <div className="pointer-events-none absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-orange-100/40 blur-3xl -translate-y-1/2 translate-x-1/4" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-bold text-orange-700 bg-orange-100 px-2.5 py-1 rounded-full mb-4">
            🇮🇳 Made in India AI Stack
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] text-[#111111]">
            The only AI workspace with <span className="text-orange-600">real Indian voice tech</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl">
            22 Indian-language speech-to-text · 40+ studio-grade Indian voices · high-accuracy translation ·
            frontier Indic chat — all inside MarkeeMix. Built for creators, teachers and marketers who serve India.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="indic-hero-voices">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Indian voice studio</p>
                <p className="mt-1 font-heading text-xl font-semibold text-[#111111]">Click any voice to preview</p>
              </div>
              <span className="text-[10px] uppercase font-bold bg-[#111111] text-white px-2 py-1 rounded">Live</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {voices.map(v => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => play(v.id)}
                  data-testid={`indic-hero-voice-${v.id}`}
                  className={`relative px-2 py-3 rounded-lg text-xs font-semibold transition-all ${
                    voicePlaying === v.id
                      ? "bg-orange-500 text-white shadow-lg"
                      : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                  }`}>
                  <div className="flex items-center justify-center gap-1">
                    {voicePlaying === v.id
                      ? <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse" />
                      : <Play className="w-3 h-3" />}
                    {v.label}
                  </div>
                  <div className={`text-[9px] mt-0.5 ${voicePlaying === v.id ? "text-white/70" : "text-slate-400"}`}>{v.gender === "F" ? "♀" : "♂"}</div>
                </button>
              ))}
            </div>
            <button
              onClick={() => navigate("/tools/sarvam-studio")}
              className="mt-5 w-full text-center text-sm font-semibold text-[#A855F7] hover:underline"
              data-testid="indic-hero-voices-cta">
              Explore all 40+ voices &amp; languages →
            </button>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="indic-hero-translate">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Live translation</p>
                <p className="mt-1 font-heading text-xl font-semibold text-[#111111]">Type anything → get Hindi</p>
              </div>
              <span className="text-[10px] uppercase font-bold bg-[#111111] text-white px-2 py-1 rounded">Demo</span>
            </div>
            <input
              type="text"
              value={demoIn}
              onChange={(e) => setDemoIn(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm mb-2"
              placeholder="Type an English sentence…"
              data-testid="indic-hero-demo-input"
            />
            <button
              onClick={translate}
              disabled={demoBusy}
              className="w-full h-9 bg-[#111111] hover:bg-[#333] text-white text-sm font-semibold rounded-md disabled:opacity-50"
              data-testid="indic-hero-demo-run">
              {demoBusy ? "Translating…" : "Translate to Hindi →"}
            </button>
            {demoOut && (
              <div className="mt-4 p-3 bg-orange-50 border-l-4 border-orange-500 rounded-r-md">
                <p className="text-[10px] uppercase tracking-widest font-bold text-orange-700 mb-1">Hindi output</p>
                <p className="text-sm text-[#111111] font-semibold" data-testid="indic-hero-demo-output">{demoOut}</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 bg-gradient-to-r from-[#111111] to-[#333333] text-white rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="indic-hero-toolkit-cta">
          <div>
            <p className="text-[11px] uppercase tracking-widest font-bold text-orange-400 mb-2">Regional Toolkit</p>
            <h3 className="font-heading text-2xl font-semibold">Ship India-first workflows in one click</h3>
            <p className="mt-2 text-sm text-slate-300 max-w-2xl">
              Regional ad copy · WhatsApp broadcast composer · Podcast → blog repurposer · Sales call analyzer —
              all built on our Made-in-India AI stack.
            </p>
          </div>
          <button
            onClick={() => navigate("/tools/regional-toolkit")}
            className="shrink-0 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg flex items-center gap-2"
            data-testid="indic-hero-toolkit-btn">
            Open Regional Toolkit
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}



export default function PremiumHomeHero() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-[#111111] font-sans" data-testid="premium-home">
      <PremiumNav />

      {/* ─── HERO ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full"
               style={{ background: "radial-gradient(circle, rgba(168,85,247,0.10) 0%, rgba(168,85,247,0) 60%)" }}/>
        </div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <motion.div className="max-w-4xl mx-auto text-center" {...fadeUp()}>
            <Pill variant="brand" icon={Sparkles} className="mx-auto" data-testid="premium-hero-eyebrow">
              The AI Workspace — for everyone who creates
            </Pill>

            <h1 className="mt-6 font-heading font-bold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#111111]" data-testid="premium-hero-title">
              The AI Workspace for
              <br/>
              <span className="text-[#A855F7]">Teachers, Creators &amp; Marketers</span>
            </h1>

            <motion.p {...fadeUp(0.08)} className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto font-sans" data-testid="premium-hero-sub">
              Plan, Create, Teach, Analyze and Grow using one intelligent AI platform. No more switching between 20 tabs.
            </motion.p>

            <motion.div {...fadeUp(0.16)} className="mt-10 flex items-center justify-center gap-3 flex-wrap" data-testid="premium-hero-ctas">
              <PremiumButton size="lg" iconRight={ArrowRight} onClick={() => navigate("/signup")} data-testid="premium-hero-cta-primary">
                Start Free
              </PremiumButton>
              <PremiumButton variant="secondary" size="lg" icon={Play} onClick={() => navigate("/youtube-suite/channel-assessment")} data-testid="premium-hero-cta-secondary">
                Try Channel Audit
              </PremiumButton>
            </motion.div>

            <motion.div {...fadeUp(0.24)} className="mt-12 flex items-center justify-center flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400 font-sans" data-testid="premium-hero-trusted">
              <span className="text-slate-400">Trusted by</span>
              {TRUSTED_BY.map((g) => (
                <span key={g} className="font-medium text-slate-500">{g}</span>
              ))}
            </motion.div>
          </motion.div>

          {/* Pillar cards */}
          <motion.div
            {...fadeUp(0.32)}
            className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
            data-testid="premium-pillar-grid"
          >
            {PILLAR_CARDS.map((c) => (
              <PremiumCard
                key={c.title}
                interactive
                onClick={() => navigate(c.to)}
                className="p-6 group"
                data-testid={`premium-pillar-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#111111] text-white flex items-center justify-center mb-5 group-hover:bg-[#A855F7] transition-colors">
                  <c.Icon className="w-5 h-5"/>
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#111111]">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-500 font-sans">{c.desc}</p>
                <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#A855F7]">
                  Explore
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform"/>
                </div>
              </PremiumCard>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── STATS STRIPE ──────────────────────────────────────────────── */}
      <section className="border-y border-slate-200/80 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            ["50+", "AI tools"],
            ["1 click", "to ship anything"],
            ["10×", "faster than ChatGPT alone"],
            ["5.0★", "rated by creators"],
          ].map(([num, label]) => (
            <div key={label}>
              <div className="font-heading text-3xl sm:text-4xl font-bold text-[#111111]">{num}</div>
              <div className="mt-1 text-sm text-slate-500 font-sans">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── MADE IN INDIA AI STACK ───────────────────────────────────── */}
      <IndicHeroSection />

      {/* ─── HOW IT WORKS ──────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="how-it-works">
        <motion.div {...fadeUp()} className="max-w-2xl">
          <span className="inline-block text-[11px] tracking-[0.18em] uppercase font-bold text-[#A855F7] mb-3">How it works</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] text-[#111111]">
            Three steps from <span className="text-[#A855F7]">idea to ship</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600">
            We removed every step that didn&apos;t matter. What&apos;s left is the work that grows your channel, classroom, or agency.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {HOW_IT_WORKS.map((s, i) => (
            <motion.div key={s.step} {...fadeUp(0.08 * i)}>
              <PremiumCard className="p-8 h-full" hover={false}>
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-xl bg-[#111111] text-white flex items-center justify-center">
                    <s.Icon className="w-5 h-5"/>
                  </div>
                  <span className="font-heading text-3xl font-bold text-slate-200">{s.step}</span>
                </div>
                <h3 className="mt-6 font-heading text-xl font-semibold text-[#111111]">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </PremiumCard>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── FEATURED TOOLS ─────────────────────────────────────────────── */}
      <section className="bg-slate-50/40 border-y border-slate-200/80">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="featured-tools">
          <motion.div {...fadeUp()} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="max-w-2xl">
              <span className="inline-block text-[11px] tracking-[0.18em] uppercase font-bold text-[#A855F7] mb-3">Featured tools</span>
              <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] text-[#111111]">
                The 6 tools you&apos;ll <span className="text-[#A855F7]">open daily</span>
              </h2>
            </div>
            <PremiumButton variant="secondary" iconRight={ArrowRight} onClick={() => navigate("/ai-studio")}>
              Browse all tools
            </PremiumButton>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURED_TOOLS.map((t, i) => (
              <motion.div key={t.title} {...fadeUp(0.04 * i)}>
                <PremiumCard
                  interactive
                  onClick={() => navigate(t.to)}
                  className="p-6 h-full group"
                  data-testid={`featured-tool-${t.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl bg-[#111111] text-white flex items-center justify-center group-hover:bg-[#A855F7] transition-colors">
                      <t.Icon className="w-4 h-4"/>
                    </div>
                    {t.badge && <Pill variant="brand">{t.badge}</Pill>}
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-[#111111]">{t.title}</h3>
                  <p className="mt-1 text-sm text-slate-500 leading-relaxed">{t.desc}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#A855F7]">
                    Open <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform"/>
                  </div>
                </PremiumCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ──────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="testimonials">
        <motion.div {...fadeUp()} className="text-center max-w-2xl mx-auto">
          <span className="inline-block text-[11px] tracking-[0.18em] uppercase font-bold text-[#A855F7] mb-3">Loved by creators</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] text-[#111111]">
            our early founding users ship faster
          </h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={t.name} {...fadeUp(0.08 * i)}>
              <PremiumCard className="p-7 h-full flex flex-col" hover={false}>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5 text-[#A855F7] fill-[#A855F7]"/>
                  ))}
                </div>
                <p className="mt-4 text-base text-slate-700 leading-relaxed flex-1">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="font-heading text-sm font-semibold text-[#111111]">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.role}</div>
                </div>
              </PremiumCard>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── FAQ ───────────────────────────────────────────────────────── */}
      <section className="bg-slate-50/40 border-y border-slate-200/80">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="faq">
          <motion.div {...fadeUp()} className="text-center">
            <span className="inline-block text-[11px] tracking-[0.18em] uppercase font-bold text-[#A855F7] mb-3">FAQ</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] text-[#111111]">
              Questions, answered
            </h2>
          </motion.div>
          <div className="mt-12">
            {FAQS.map((f, i) => <FAQItem key={f.q} {...f} idx={i}/>)}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="final-cta">
        <motion.div {...fadeUp()} className="relative overflow-hidden rounded-3xl bg-[#111111] text-white px-8 sm:px-16 py-20 sm:py-24 text-center">
          {/* Subtle glow */}
          <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px]"
               style={{ background: "radial-gradient(ellipse, rgba(168,85,247,0.35) 0%, rgba(168,85,247,0) 60%)" }}/>

          <div className="relative">
            <Pill variant="dark" icon={Sparkles} className="mx-auto bg-white/10 text-white">
              No credit card required
            </Pill>
            <h2 className="mt-6 font-heading text-4xl sm:text-6xl font-bold tracking-tight leading-[1.05]">
              Start shipping with AI <br/>
              <span className="text-[#A855F7]">today.</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-white/65 max-w-xl mx-auto">
              Free forever for hobbyists. Upgrade only when you need unlimited generations.
            </p>
            <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={() => navigate("/signup")}
                data-testid="final-cta-primary"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#A855F7] hover:bg-[#9333EA] text-white text-base font-semibold transition-colors shadow-glow"
              >
                Start free <ArrowRight className="w-4 h-4"/>
              </button>
              <button
                onClick={() => navigate("/pricing")}
                data-testid="final-cta-secondary"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl border border-white/15 hover:border-white/30 text-base font-semibold transition-colors"
              >
                See pricing
              </button>
            </div>
            <div className="mt-8 flex items-center justify-center flex-wrap gap-x-6 gap-y-2 text-xs text-white/50">
              <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400"/> Free forever tier</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400"/> No credit card</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400"/> Cancel anytime</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ─── FOOTER ─────────────────────────────────────────────────────── */}
      <PremiumFooter />
    </div>
  );
}
